import React, { useState } from 'react'
import { Classes, Dialog } from '@blueprintjs/core'
import { Flex, Icon, Toaster, WPGSelect } from '@ui'
import { pluralize } from '@utils'
import api, { IWorkplaceGroup } from '@api'
import { useFloor } from '@providers/mapProvider'
import css from './workplaceGroupPopup.module.scss'
import { SelectWrapper } from 'components/AdminFloorPlan/components/SelectWrapper'
import { EvoGhostButton, EvoButton } from '@ui/buttons/EvoButton'
import { Button } from '@runeffective/evo-design-system'
import { Checkbox } from '@runeffective/evo-design-system'

export interface WorkplaceGroupPopupDialogProps {
  setIsWorkplaceGroupOpen: (para: boolean) => void
  isWorkplaceGroupOpen: boolean
  selectedSpaces: string[]
  allEmployeesList: string[]
}

export default function WorkplaceGroupPopup({
  setIsWorkplaceGroupOpen,
  isWorkplaceGroupOpen,
  selectedSpaces,
  allEmployeesList,
}: WorkplaceGroupPopupDialogProps) {
  const [needAssignEmployees, setneedAssignEmployees] = useState(false)
  const [needAssignSpaces, setneedAssignSpaces] = useState(false)
  const [btnDisabled, setbtnDisabled] = useState(true)
  const [selectedWpg, setselectedWpg] = useState(null as IWorkplaceGroup | null)
  const floor = useFloor()
  const apId = floor.apId

  function btnEnableDisaled(employeeChecked: boolean, spaceChecked: boolean, workplace: any) {
    if ((employeeChecked || spaceChecked) && workplace?.id) {
      setbtnDisabled(false)
    } else {
      setbtnDisabled(true)
    }
  }

  function clearState() {
    setbtnDisabled(true)
    setselectedWpg(null)
    setneedAssignSpaces(false)
    setneedAssignEmployees(false)
  }

  function toggleAssignEmployees() {
    const checked = !needAssignEmployees
    setneedAssignEmployees(checked)
    btnEnableDisaled(checked, needAssignSpaces, selectedWpg)
  }

  function toggleAssignSpaces() {
    const checked = !needAssignSpaces
    setneedAssignSpaces(checked)
    btnEnableDisaled(needAssignEmployees, checked, selectedWpg)
  }

  function onClickAssign() {
    if (selectedWpg) {
      if (needAssignSpaces) {
        api.workplaceGroup.addSpaces(selectedWpg.id, selectedSpaces).then(() => {
          Toaster.success({ message: 'Spaces were assigned to workplace group' })
          setIsWorkplaceGroupOpen(false)
          floor.reloadMap?.()
        })
      }
      if (needAssignEmployees) {
        api.workplaceGroup.addEmployees(selectedWpg.id, allEmployeesList).then(() => {
          Toaster.success({ message: 'Employee were assigned to workplace group' })
          setIsWorkplaceGroupOpen(false)
          floor.reloadMap?.()
        })
      }
      setIsWorkplaceGroupOpen(false)
    }
    clearState()
  }

  function onClickUnAssign() {
    if (allEmployeesList.length > 0) {
      api.workplaceGroup.removeEmployees(allEmployeesList).then(() => {
        Toaster.success({ message: 'Employee were unassigned from workplace group' })
        setIsWorkplaceGroupOpen(false)
        floor.reloadMap?.()
      })
    }

    if (selectedSpaces.length > 0) {
      api.workplaceGroup.removeWorkplaceGroupsFromSpaces(selectedSpaces).then(() => {
        Toaster.success({ message: 'Spaces were unassigned from workplace group' })
        setIsWorkplaceGroupOpen(false)
        floor.reloadMap?.()
      })
    }
    clearState()
  }

  return (
    <>
      <Dialog
        style={{ paddingBottom: '0px' }}
        isOpen={isWorkplaceGroupOpen}
        className={css.dialogWidth}
        transitionDuration={200}
        isCloseButtonShown={true}
        canOutsideClickClose={false}
        onClose={() => setIsWorkplaceGroupOpen(false)}>
        <div className={css.empList}>
          <Button
            style={{ position: 'absolute', top: '5px', right: '10px' }}
            aria-label="single icon button"
            onClick={() => {
              setIsWorkplaceGroupOpen(false)
            }}
            size="default"
            layout="borderless">
            <Icon name="close" />
          </Button>

          <div>
            <div className={css.dialogHeader}> Workplace Groups </div>
          </div>
          <div>
            <div className={css.labelText}>
              Assign the selected employees and/or seats to the appropriate workplace group
            </div>
            <div className={css.paddingTop}>
              <Checkbox
                disabled={allEmployeesList.length == 0 ? true : false}
                label={`Assign ${!allEmployeesList.length ? '' : allEmployeesList.length} employee
              ${pluralize(allEmployeesList.length)} to workplace group`}
                checked={needAssignEmployees}
                onChange={() => {
                  toggleAssignEmployees()
                }}
              />
              <div className={css.emptyDiv}></div>
              <Checkbox
                label={`Assign ${selectedSpaces.length} seat ${pluralize(selectedSpaces.length)} to
                workplace group`}
                checked={needAssignSpaces}
                onChange={() => {
                  toggleAssignSpaces()
                }}
              />
            </div>
            <div className={css.paddingTop}>
              <SelectWrapper title="Workplace Group">
                <WPGSelect
                  clearable={false}
                  value={selectedWpg}
                  canCreate={true}
                  filterable={true}
                  buttonProps={{ style: { width: 270, borderColor: '#848995' } }}
                  onCreate={(name) => {
                    api.workplaceGroup.create({ name }).then((workplaceGroup) => {
                      setselectedWpg(workplaceGroup)
                      btnEnableDisaled(needAssignEmployees, needAssignSpaces, workplaceGroup)
                    })
                  }}
                  onChange={(data: React.SetStateAction<IWorkplaceGroup | null>) => {
                    setselectedWpg(data)
                    btnEnableDisaled(needAssignEmployees, needAssignSpaces, data)
                  }}
                />
              </SelectWrapper>
            </div>
            <div className={css.unAssignMsgLabel}>
              <Icon name="fa fa-exclamation-triangle" style={{ color: '#804e00' }}></Icon>
              <span> Unassign workplace group for all selected seats and employees </span>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={css.emptyDiv}>
              <div className={css.row}>
                <div className={css.leftPanel}>
                  <Button layout="danger" onClick={() => onClickUnAssign()} size="default">
                    Unassign
                  </Button>
                </div>
                <div className={css.rightPanel}>
                  <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Flex style={{ gap: '8px' }} direction="row" justify="space-between">
                      <Button
                        layout="borderless"
                        onClick={() => {
                          setIsWorkplaceGroupOpen(false)
                          clearState()
                        }}
                        size="default">
                        Cancel
                      </Button>
                      <Button
                        disabled={btnDisabled}
                        layout="primary"
                        onClick={() => onClickAssign()}
                        size="default">
                        Update
                      </Button>
                    </Flex>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}
